import React from 'react';
import { Space, Button, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { CustomAttributesDiffCell } from '@components/shared/inventory/InventoryDiffCell';

/**
 * Shared column definitions for StockAdjustment item tables (New / Edit pages).
 *
 * @param {Function} t - i18n translation function
 * @param {Object}   [options]
 * @param {Function} [options.onRemove]     - callback(index) to remove an item; omit to hide the action column
 * @param {Function} [options.onNoteChange] - callback(index, value) to edit note inline; omit to render note as text
 * @param {Object}   [options.attributeLabelByName] - map of attribute name -> human label, for custom_attributes diff cell
 */
export function getStockAdjustmentItemColumns(t, { onRemove, onNoteChange, attributeLabelByName } = {}) {
  const columns = [
    {
      title: t('models.product.name'),
      render: (_, r) => {
        const productId = r.inventory?.product?.id || r.inventory?.product_id;
        const sku = r.inventory?.product?.sku || r.inventory?.product_sku;
        const name = r.inventory?.product?.name || r.inventory?.product_name;
        if (!sku) return '-';
        const content = (
          <>
            <div>{sku}</div>
            {name && <div style={{ fontSize: 12, color: '#666' }}>{name}</div>}
          </>
        );
        return productId ? <Link to={`/products/${productId}`} target="_blank">{content}</Link> : content;
      },
    },
    {
      title: t('models.shelf.location'),
      render: (_, r) => r.shelf?.shelf_location || r.shelf?.shelf_code || '-',
    },
    {
      title: t('models.inventory.inventory_type'),
      render: (_, r) => r.inventory?.inventory_type?.name || '-',
    },
    {
      title: t('models.inventory.batch'),
      render: (_, r) => r.inventory?.batch || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      render: (_, r) => r.inventory?.expiration_date || '-',
    },
    {
      title: t('models.inventory.custom_attributes', { defaultValue: '自訂屬性' }),
      render: (_, r) => <CustomAttributesDiffCell source={r.inventory?.custom_attributes} attributeLabelByName={attributeLabelByName} />,
      ellipsis: true,
    },
    {
      title: t('models.stock_adjustment.quantity'),
      render: (_, r) => {
        if (r.system_quantity != null) {
          const isGain = r.quantity > 0;
          return (
            <Space direction="vertical" size={0}>
              <span style={{ fontSize: 12, color: '#999' }}>{r.system_quantity} → {r.actual_quantity}</span>
              <span style={{ color: isGain ? '#52c41a' : '#ff4d4f' }}>
                {isGain ? `+${r.quantity}` : r.quantity}
              </span>
            </Space>
          );
        }
        const isGain = r.quantity > 0;
        return (
          <span style={{ color: isGain ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
            {isGain ? `+${r.quantity}` : r.quantity}
          </span>
        );
      },
    },
    {
      title: t('models.stock_adjustment.reason'),
      render: (_, r) => r.reason_code ? t(`enums.stock_adjustment.reason_code.${r.reason_code}`, r.reason_code) : '-',
    },
    {
      title: t('models.stock_adjustment.note'),
      width: 200,
      render: (_, r, index) => onNoteChange ? (
        <Input
          value={r.note || ''}
          onChange={(e) => onNoteChange(index, e.target.value)}
          placeholder={t('models.stock_adjustment.note')}
          size="small"
        />
      ) : (r.note || '-'),
      ellipsis: !onNoteChange,
    },
  ];

  if (onRemove) {
    columns.push({
      title: t('common.actions'),
      width: 60,
      render: (_, _r, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRemove(index)}
        />
      ),
    });
  }

  return columns;
}
